import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getUserWithTeam, getTeamMemberTotalsByLeague, getAllTransactionsByStatus, getPortefeuilleData, logoutUser, unmarkTutoPhaseSeen } from '../../Api/authApi.js.js';
import { updateUser } from '../../redux/authSlice';
import Navbarfooter from "../../components/Navbarfooter/index.js";
import { tutosConfig } from '../../tutos/tutosConfig';
import './profil.css';

const PALETTE = ['#7AB8E0', '#9B7BE0', '#E0C281', '#7AE0C5', '#E07AAB', '#4ADE9E', '#FF7A78'];

const nameColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return PALETTE[h % PALETTE.length];
};

const initials = (name = '') =>
  name.split(/\s+/).map(w => w[0] || '').join('').toUpperCase().slice(0, 2) || '?';

const fmtPct = (n) => {
  const v = parseFloat(n) || 0;
  return (v >= 0 ? '+' : '') + v.toFixed(2) + ' %';
};

const fmtSeenAt = (iso) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

const Profil = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  const [teamName, setTeamName] = useState('');
  const [leagueName, setLeagueName] = useState('');
  const [rang, setRang] = useState(null);
  const [tradesCount, setTradesCount] = useState(null);
  const [reussite, setReussite] = useState(null);
  const [portefeuilleData, setPortefeuilleData] = useState(null);
  const [capitalHistory, setCapitalHistory] = useState([]);
  const [tutoOpen, setTutoOpen] = useState(false);

  useEffect(() => {
    getUserWithTeam(user.id)
      .then(data => {
        setTeamName(data.team_name || '');
        setLeagueName(data.league_name || '');
      })
      .catch(() => {});
  }, [user.id]);

  useEffect(() => {
    getTeamMemberTotalsByLeague(user.id)
      .then(res => {
        const individual = res.individualRanking || [];
        const me = individual.find(p => p.user_id === user.id);
        if (me) setRang(me.rank);
      })
      .catch(() => {});
  }, [user.id]);

  useEffect(() => {
    Promise.all([
      getAllTransactionsByStatus(user.id).catch(() => ({ transactions: [] })),
      getPortefeuilleData(user.id).catch(() => null),
    ]).then(([txRes, pfData]) => {
      const txs = txRes.transactions || [];
      const total = txs.length;
      const profitable = txs.filter(t => (parseFloat(t.gain_loss) || 0) > 0).length;
      setTradesCount(total);
      setReussite(total > 0 ? { pct: Math.round((profitable / total) * 100), profit: profitable, total } : null);
      setPortefeuilleData(pfData);

      if (pfData) {
        const initial = parseFloat(pfData.montant_initial) || 0;
        const current = parseFloat(pfData.montant_actuel) || 0;
        const sorted = [...txs]
          .filter(t => parseFloat(t.gain_loss) !== 0)
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        let running = initial;
        const history = [initial];
        for (const tx of sorted) {
          running += parseFloat(tx.gain_loss) || 0;
          history.push(running);
        }
        // Dernier point = capital réel actuel (inclut positions ouvertes)
        history.push(current);
        setCapitalHistory(history);
      }
    });
  }, [user.id]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };

  // "Revoir un tuto" : si la phase a déjà été vue, retire son seenAt de tutos_seen pour qu'elle se
  // redéclenche ; sinon rien à retirer, TutoRunner l'affichera de toute façon normalement. Dans les
  // deux cas on navigue sur la route concernée (page ou modal détectée ensuite par TutoRunner).
  const handleRevoirTuto = async (phase) => {
    const alreadySeen = Boolean(user.tutos_seen?.[phase.name]?.seenAt);
    if (alreadySeen) {
      try {
        const data = await unmarkTutoPhaseSeen(user.id, phase.name);
        dispatch(updateUser({ tutos_seen: data.tutos_seen }));
      } catch (error) {
        toast.warning(error.message || "Impossible de relancer ce tutoriel pour le moment.");
        return;
      }
    }
    navigate(phase.page || phase.revisitPath, { state: { reopenTutoPhase: phase.name } });
  };

  const avatarColor = nameColor(user.username || '');

  return (
    <div className="profil-page !pt-2 xl:!pt-5 max-w-[850px] xl:mx-5">

      {/* ── Hero card */}
      <div className="relief" style={{ borderRadius: 24, padding: 18, position: 'relative', overflow: 'hidden', marginTop: 4 }}>
        <div className="profil-hero-backdrop" />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Avatar */}
          <div style={{
            width: 64, height: 64, borderRadius: 32, flexShrink: 0,
            background: `linear-gradient(180deg, ${avatarColor}55 0%, ${avatarColor}22 100%)`,
            border: `2px solid ${avatarColor}`,
            boxShadow: `0 0 0 3px ${avatarColor}22`,
            display: 'grid', placeItems: 'center',
            color: avatarColor, fontWeight: 700, fontSize: 24,
          }}>{initials(user.username)}</div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.username}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2, display: 'flex', gap: 6, alignItems: 'center' }}>
              {teamName && <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{teamName}</span>}
              {teamName && leagueName && <span style={{ color: 'var(--text-mute)' }}>·</span>}
              {leagueName && <span>{leagueName}</span>}
            </div>
          </div>
        </div>

        {/* Mini stats */}
        <div className="profil-mini-stats">
          <MiniStat label="Rang" value={rang != null ? `#${rang}` : '—'} />
          <MiniStat label="Trades" value={tradesCount != null ? String(tradesCount) : '—'} />
          <MiniStat
            label="Réussite"
            value={reussite ? `${reussite.pct}%` : '—'}
            hint={reussite ? `${reussite.profit}/${reussite.total}` : null}
          />
        </div>
      </div>

      {/* ── Mes informations */}
      <div className="profil-section-header">
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>Mes informations</h2>
      </div>
      <div className="relief" style={{ borderRadius: 18, overflow: 'hidden' }}>
        <InfoRow icon="user" label="Nom d'utilisateur" value={user.username} action={<Link to="/modifie-nom-utilisateur"><ActionBtn>Modifier</ActionBtn></Link>} />
        <InfoRow icon="mail" label="Email" value={user.email} action={<ActionBtn muted>Vérifié</ActionBtn>} />
        <InfoRow icon="lock" label="Mot de passe" value="••••••••••" action={<Link to="/modifie-mot-de-passe"><ActionBtn>Modifier</ActionBtn></Link>} />
        <InfoRow icon="team" label="Équipe" value={teamName || '—'} action={<Link to="/modifie-nom-equipe"><ActionBtn>Changer</ActionBtn></Link>} last />
      </div>

      {/* ── Mes statistiques */}
      <div className="profil-section-header">
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>Mes statistiques</h2>
      </div>
      <div className="relief" style={{ borderRadius: 20, padding: 16 }}>
        {portefeuilleData && (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Évolution capital</div>
                <div className="num" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 2 }}>
                  {parseFloat(portefeuilleData.montant_actuel).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span style={{ color: 'var(--text-dim)', fontWeight: 500, fontSize: 18 }}> €</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="num" style={{
                  fontSize: 14, fontWeight: 600,
                  color: parseFloat(portefeuilleData.difference_percentage) >= 0 ? 'var(--up)' : 'var(--down)',
                }}>{fmtPct(portefeuilleData.difference_percentage)}</div>
                <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 2 }}>depuis le début</div>
              </div>
            </div>
            {capitalHistory.length >= 2 && (
              <div style={{ marginTop: 10, marginLeft: -8, marginRight: -8 }}>
                <AreaChart
                  data={capitalHistory}
                  up={parseFloat(portefeuilleData.difference_percentage) >= 0}
                  height={110}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Aide */}
      <div className="profil-section-header">
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>Aide</h2>
      </div>
      <div className="relief" style={{ borderRadius: 18, overflow: 'hidden' }}>
        <button
          className="profil-accordion-header"
          onClick={() => setTutoOpen(o => !o)}
          aria-expanded={tutoOpen}
        >
          <div className="profil-info-icon">
            <InfoIcon name="tuto" />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'left' }}>Revoir un tuto</span>
          <svg className={`profil-accordion-chevron${tutoOpen ? ' open' : ''}`} width={14} height={14} viewBox="0 0 14 14" fill="none">
            <path d="M3 5.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className={`profil-accordion-panel${tutoOpen ? ' open' : ''}`}>
          {tutosConfig.map((phase) => {
            const seenAt = user.tutos_seen?.[phase.name]?.seenAt;
            return (
              <button
                key={phase.name}
                className="profil-accordion-item pill-tap hover:bg-black/10"
                onClick={() => handleRevoirTuto(phase)}
              >
                <span>{phase.label}</span>
                <span className={`profil-accordion-item-date${seenAt ? '' : ' unseen'}`}>
                  {seenAt ? 'Vu le ' + fmtSeenAt(seenAt) : 'Pas encore vu'}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Déconnexion */}
      <button className="profil-logout-btn pill-tap" onClick={handleLogout}>
        Se déconnecter
      </button>

      <Navbarfooter />
    </div>
  );
};

/* ── Sub-components */

function AreaChart({ data, height = 110, up = true }) {
  const w = 340, pad = 6;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const iw = w - pad * 2;
  const ih = height - pad * 2;
  const stepX = iw / (data.length - 1);
  const pts = data.map((v, i) => [pad + i * stepX, pad + ih - ((v - min) / range) * ih]);
  const path = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${path} L${pad + iw},${pad + ih} L${pad},${pad + ih} Z`;
  const c = up ? 'var(--up)' : 'var(--down)';
  const gradId = 'pf-area-grad';
  const last = pts[pts.length - 1];
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${height}`} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c} stopOpacity="0.28"/>
          <stop offset="100%" stopColor={c} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <g opacity="0.12">
        {[0.25, 0.5, 0.75].map(t => (
          <line key={t} x1={pad} x2={pad + iw} y1={pad + ih * t} y2={pad + ih * t}
                stroke="white" strokeWidth="0.5" strokeDasharray="2 3"/>
        ))}
      </g>
      <path d={area} fill={`url(#${gradId})`}/>
      <path d={path} fill="none" stroke={c} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"/>
      <circle cx={last[0]} cy={last[1]} r="3.5" fill={c}/>
      <circle cx={last[0]} cy={last[1]} r="6" fill={c} opacity="0.2"/>
    </svg>
  );
}

function MiniStat({ label, value, hint }) {
  return (
    <div style={{ background: 'var(--surface)', padding: '10px 12px' }}>
      <div style={{ fontSize: 9.5, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 2 }}>
        <span className="num" style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.015em' }}>{value}</span>
        {hint && <span style={{ fontSize: 10, color: 'var(--up)', fontWeight: 600, marginLeft: 5 }}>{hint}</span>}
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value, action, last }) {
  return (
    <div className={`profil-info-row${last ? ' last' : ''}`} style={{ borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <div className="profil-info-icon">
        <InfoIcon name={icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--text-mute)', fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 500, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
      </div>
      {action}
    </div>
  );
}

function ActionBtn({ children, muted }) {
  return (
    <button className="pill-tap" style={{
      background: muted ? 'var(--up-soft)' : 'rgba(255,255,255,0.06)',
      border: '1px solid ' + (muted ? 'rgba(74,222,158,0.2)' : 'var(--border)'),
      color: muted ? 'var(--up)' : 'var(--text)',
      fontSize: 11, fontWeight: 600,
      padding: '6px 10px',
      borderRadius: 8, cursor: 'pointer',
      fontFamily: 'inherit',
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function InfoIcon({ name }) {
  const c = 'currentColor', w = 14, h = 14;
  switch (name) {
    case 'user': return <svg width={w} height={h} viewBox="0 0 14 14"><circle cx="7" cy="5" r="2.5" stroke={c} strokeWidth="1.4" fill="none"/><path d="M2 12c0-2.5 2.2-4 5-4s5 1.5 5 4" stroke={c} strokeWidth="1.4" fill="none"/></svg>;
    case 'mail': return <svg width={w} height={h} viewBox="0 0 14 14"><rect x="1.5" y="3" width="11" height="8" rx="1.5" stroke={c} strokeWidth="1.4" fill="none"/><path d="M2 4l5 4 5-4" stroke={c} strokeWidth="1.4" fill="none"/></svg>;
    case 'lock': return <svg width={w} height={h} viewBox="0 0 14 14"><rect x="2" y="6" width="10" height="7" rx="1.5" stroke={c} strokeWidth="1.4" fill="none"/><path d="M4 6V4a3 3 0 016 0v2" stroke={c} strokeWidth="1.4" fill="none"/></svg>;
    case 'team': return <svg width={w} height={h} viewBox="0 0 14 14"><circle cx="5" cy="5" r="2" stroke={c} strokeWidth="1.4" fill="none"/><circle cx="10" cy="5" r="2" stroke={c} strokeWidth="1.4" fill="none"/><path d="M1 12c0-2 1.5-3 4-3s4 1 4 3M8 9c1.7 0 5 1 5 3" stroke={c} strokeWidth="1.4" fill="none"/></svg>;
    case 'tuto': return <svg width={w} height={h} viewBox="0 0 14 14"><circle cx="7" cy="7" r="5.5" stroke={c} strokeWidth="1.4" fill="none"/><path d="M7 10.2v-.1M7 8.3c0-1.2 1.4-1.2 1.4-2.4a1.4 1.4 0 00-2.8 0" stroke={c} strokeWidth="1.4" fill="none" strokeLinecap="round"/></svg>;
    default: return null;
  }
}

export default Profil;
